
import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/api";
import Button from "../../components/ui/Button";
import InputWithIcon from "../../components/ui/InputWithIcon";

const AddStockPage = ({ setView, setEditingContext }) => {
  const { user } = useAuth();

  const [stockTypes, setStockTypes] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filteredStockTypes, setFilteredStockTypes] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [orderDate, setOrderDate] = useState(
    () => new Date().toISOString().slice(0, 10)
  );

  const [godowns, setGodowns] = useState([]);
  const [selectedGodown, setSelectedGodown] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 🔹 Get GdId from sessionStorage
  const gdId = sessionStorage.getItem("gdid");

  // 🔹 Load stock type list
  useEffect(() => {
    const fetchStockTypes = async () => {
      try {
        setLoading(true);
        const data = await api.getstockTypeList(user.UsrName);
        if (data) {
          setStockTypes(data.Table || []);
        }
      } catch (err) {
        console.error("❌ Failed to fetch stock list:", err);
        setError("Failed to fetch stock list.");
      } finally {
        setLoading(false);
      }
    };
    fetchStockTypes();
  }, [user.UsrName]);

  // 🔹 Search filter
  useEffect(() => {
    if (searchText) {
      const results = stockTypes.filter((s) =>
        s.Vname?.toLowerCase().includes(searchText.toLowerCase())
      );
      const exactMatch = selectedStock && selectedStock.Vname === searchText;
      setFilteredStockTypes(exactMatch ? [] : results);
      setIsDropdownOpen(!exactMatch && results.length > 0);
    } else {
      setFilteredStockTypes([]);
      setIsDropdownOpen(false);
    }
  }, [searchText, stockTypes, selectedStock]);

  // 🔹 Select stock type → fetch godowns
  const handleStockSelect = async (stock) => {
    setSelectedStock(stock);
    setSearchText(stock.Vname);
    setIsDropdownOpen(false);
    setFilteredStockTypes([]);
    setError("");

    try {
      const res = await api.StkAdjGodownList(stock.Vcode, gdId);
      console.log("👉 Godown API Response:", res);

      const gdList = res?.StkAdjGodownListAPIjs?.Table || res?.Table || [];

      const normalized = gdList.map((g, i) => ({
        id: g.StkGdId ?? i,
        name: g.StkGodownName ?? `Godown-${i}`,
      }));

      if (normalized.length > 0) {
        setGodowns(normalized);
        setSelectedGodown(normalized[0].id.toString()); // auto-select first
      } else {
        setGodowns([{ id: 0, name: "N/A" }]);
        setSelectedGodown("0");
      }
    } catch (err) {
      console.error("❌ Failed to fetch godowns:", err);
      setError("Failed to fetch godowns.");
    }
  };

  // 🔹 Create stock document
  const handleCreateStock = async () => {
    if (!selectedStock) {
      setError("Please select a stock type.");
      return;
    }

    if (!gdId) {
      setError("Godown not selected. Please select a godown first.");
      return;
    }

    if (!selectedGodown) {
      setError("Please select a godown.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const payload = {
        Vid: selectedStock.Vid,
        VName: selectedStock.Vname,
        VCode: selectedStock.Vcode,
        StockDate: orderDate, // already in YYYY-MM-DD
        user: user.UsrName,
        SessionId: user.SessionId || "9944010155",
        GdId: Number(gdId),
        Stkgdid: Number(selectedGodown),
      };

      console.log("👉 CreateStock payload:", payload);

      const createResult = await api.CreateStocklist(payload);
      console.log("👉 CreateStock API Response:", createResult);

      const poId =
        createResult?.CreateStocklistjs?.Table?.[0]?.POId ||
        createResult?.Table?.[0]?.POId ||
        createResult?.POId;

      if (!poId) throw new Error("No POId returned from CreateStocklist API");

      // Fetch created stock details
      const stockRes = await api.getselectedStock(poId);
      const orderHeader = stockRes?.Table?.[0] || {};
      const orderItems = stockRes?.Table1 || [];

      // ✅ Ensure Godown Name is preserved
      const godownObj = godowns.find(
        (g) => String(g.id) === String(selectedGodown)
      );

      const enrichedHeader = {
        ...orderHeader,
        StkGdId: selectedGodown,
        StkGodownName: godownObj?.name || orderHeader.StkGodownName || "-",
      };

      setEditingContext({
        id: poId,
        stockId: selectedStock.Vid,
        orderDate,
        stock: enrichedHeader,
        items: orderItems,
        modes: [],
      });

      setView("edit-stock");
    } catch (err) {
      console.error("❌ Error in handleCreateStock:", err);
      setError("Could not create stock. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Label for godown
  const getGodownLabel = () => {
    if (!selectedStock) return "Godown*";
    const name = selectedStock.Vname.toLowerCase();
    if (name.includes("inward")) return "From Godown*";
    if (name.includes("outward")) return "To Godown*";
    if (name.includes("waste")) return "Godown*";
    return "Godown*";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4 bg-white shadow">
        <h2 className="text-xl font-bold sm:text-2xl text-center">
          Create Document
        </h2>
      </div>

      <div className="flex justify-center mt-6">
        <div className="w-full max-w-lg p-4 bg-white shadow-lg sm:p-6 rounded-xl">
          {/* Search Stock */}
          <div className="relative mb-4">
            <InputWithIcon
              label="Search & Select Type*"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setSelectedStock(null);
              }}
              onFocus={() => {
                if (filteredStockTypes.length > 0) setIsDropdownOpen(true);
              }}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              }
            />
            {isDropdownOpen && filteredStockTypes.length > 0 && (
              <ul className="absolute z-10 w-full mt-1 overflow-y-auto text-sm bg-white border rounded-lg shadow-lg max-h-48 sm:max-h-60 sm:text-base">
                {filteredStockTypes.map((s) => (
                  <li
                    key={s.Vid}
                    onMouseDown={() => handleStockSelect(s)}
                    className="px-3 py-3 border-b cursor-pointer hover:bg-gray-100 last:border-none"
                  >
                    <p className="font-semibold">{s.Vname}</p>
                    <p className="text-xs text-gray-500">{s.Vcode}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Order Date */}
          <div className="mb-4">
            <InputWithIcon
              label="Order Date*"
              type="date"
              value={orderDate}
              onChange={(e) => setOrderDate(e.target.value)}
            />
          </div>

          {/* Godown selection */}
          {selectedStock && (
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">
                {getGodownLabel()}
              </label>
              <select
                value={selectedGodown}
                onChange={(e) => setSelectedGodown(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                {godowns.length === 0 ? (
                  <option value="0">N/A</option>
                ) : (
                  godowns.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))
                )}
              </select>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-2 pt-2 justify-start">
            <Button
              onClick={handleCreateStock}
              className="px-3 py-1 text-sm"
              disabled={loading}
            >
              {loading ? "Creating..." : "Create Doc"}
            </Button>
            <Button
              onClick={() => setView("list")}
              className="px-3 py-1 text-sm bg-gray-500 hover:bg-gray-600"
            >
              Cancel
            </Button>
          </div>

          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default AddStockPage;
